<script setup lang="ts">
import { computed } from 'vue';
import { type SerializedOffer } from '@models/offer.ts';
import IconPriceDropped from '@components/offer-card/icons/IconPriceDropped.vue';
import Typography from '@components/Typography.vue';
import { trans } from '@elements/translation';
import { currencyNoDecimal } from '@elements/number-formatter';
import type { Color } from '@global-types/colors.ts';

const props = withDefaults(defineProps<{
  offer: SerializedOffer,
  price: number,
  isSingleOpenspace: boolean,
  unitSeparator?: string,
  color?: Color,
}>(), {
  unitSeparator: trans('global.by'),
});

const isPriceDropRecent = computed<boolean>(() => props.offer.is_price_drop_recent);

const priceColor = computed<Color>(() => {
  if (props.color) {
    return props.color;
  }

  return isPriceDropRecent.value ? 'green' : 'black';
});
</script>

<template>
  <div class="offer-price">
    <Typography
      class="offer-price__amount"
      variant="content-heading-2"
      :color="priceColor"
    >
      {{ currencyNoDecimal(props.price) }}

      <IconPriceDropped v-if="isPriceDropRecent" />
    </Typography>

    <Typography
      variant="body-small"
      :color="color ?? 'gray-darker'"
    >
      {{ trans('global.price_tax') }} {{ unitSeparator }}

      <template v-if="isSingleOpenspace">
        {{ trans('offer.post_price') }} {{ unitSeparator }} {{ trans('global.duration.month') }}
      </template>

      <template v-else>
        {{ trans('global.duration.month') }}
      </template>
    </Typography>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.offer-price {
  display: flex;
  gap: rem(8);
  align-items: center;
  height: auto;
  white-space: nowrap;

  &__amount {
    display: flex;
    align-items: center;
    gap: rem(4);
  }
}
</style>
